import { useQuery } from '@apollo/client';
import { usePagination } from '@crehana/react-hooks';
import { notEmpty } from '@crehana/utils';

import useDebounce from '@/views/AdminDashboard/hooks/useDebounce';

import GET_ORGANIZATION_USERS_BY_STATUS from './graphql/GetOrganizationUsersByStatus.b2b.graphql';
import type {
  GetOrganizationUsersByStatus,
  GetOrganizationUsersByStatusVariables,
} from './graphql/types/GetOrganizationUsersByStatus';
import type { UserStatus } from './types';

const USERS_PER_FETCH = 6;

const usePendingUsers = ({
  slug,
  userStatus,
}: {
  slug: string;
  userStatus: UserStatus | null;
}) => {
  const { debounceValue, handleChange } = useDebounce();

  const { data, error, refetch, loading } = useQuery<
    GetOrganizationUsersByStatus,
    GetOrganizationUsersByStatusVariables
  >(GET_ORGANIZATION_USERS_BY_STATUS, {
    variables: {
      slug,
      userStatus: [1],
      first: USERS_PER_FETCH,
      after: '',
      searchKey: debounceValue,
    },
    skip: !userStatus,
    context: { clientName: 'b2b' },
    fetchPolicy: 'no-cache',
  });

  const userList =
    data?.organization?.users?.edges
      ?.map(user => user?.node)
      .filter(notEmpty) || [];

  const totalCount = data?.organization?.users?.totalCount || 0;

  const { currentPage, onPageChange, totalPages } = usePagination(
    USERS_PER_FETCH,
    {
      refetch,
      totalElements: totalCount,
    },
  );

  return {
    error,
    userList,
    totalCount,
    totalPages,
    currentPage,
    debounceValue,
    loading,
    refetch,
    onPageChange,
    handleChange,
  };
};

export default usePendingUsers;
